"use client";

import { CheckIcon, LinkIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

function LinkedInIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8h4.56v14H.22V8zm7.5 0h4.37v1.92h.06c.61-1.15 2.1-2.36 4.32-2.36 4.62 0 5.47 3.04 5.47 7v7.44h-4.56v-6.6c0-1.57-.03-3.6-2.19-3.6-2.19 0-2.53 1.71-2.53 3.48V22H7.72V8z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.83l-5.36-7.01L4.6 22H1.34l8.03-9.18L1 2h6.99l4.83 6.39L18.244 2zm-2.39 18.05h1.89L7.27 3.84H5.24l10.614 16.21z" />
    </svg>
  );
}

const iconButton =
  "border-marketing-border text-marketing-text hover:bg-marketing-cream inline-flex size-8 cursor-pointer items-center justify-center rounded-md border transition-colors";

/** "Follow {author} on LinkedIn" and share links, under the post body. */
export function PostShare({
  url,
  title,
  authorFirstName,
  authorImage,
  authorLinkedin,
}: {
  url: string;
  title: string;
  authorFirstName: string;
  authorImage: string | null;
  authorLinkedin: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const encoded = encodeURIComponent(url);

  return (
    <div className="text-marketing-text-muted flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[13px]">
      {authorLinkedin ? (
        <Link
          href={authorLinkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-marketing-text inline-flex items-center gap-2 transition-colors"
        >
          {authorImage ? (
            <Image
              src={authorImage}
              alt=""
              width={24}
              height={24}
              className="size-6 rounded-full object-cover"
            />
          ) : null}
          Follow {authorFirstName} on LinkedIn
        </Link>
      ) : null}
      <div className="flex items-center gap-2">
        <span>Share this article</span>
        <Link
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn (opens in new tab)"
          className={iconButton}
        >
          <LinkedInIcon />
        </Link>
        <Link
          href={`https://x.com/intent/post?url=${encoded}&text=${encodeURIComponent(title)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X (opens in new tab)"
          className={iconButton}
        >
          <XIcon />
        </Link>
        <button
          type="button"
          aria-label={copied ? "Link copied" : "Copy link"}
          onClick={() => {
            void navigator.clipboard.writeText(url).then(() => {
              setCopied(true);
              window.setTimeout(() => {
                setCopied(false);
              }, 2000);
            });
          }}
          className={iconButton}
        >
          {copied ? (
            <CheckIcon className="size-3.5" aria-hidden="true" />
          ) : (
            <LinkIcon className="size-3.5" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}
